import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { PLANS } from '../../analytics/eCommerce';
import { selectEventsData, setEventDataList, setAnalyticsData } from '../events/eventsSlice';
import { selectPlan, setDefaultPlanId, setPlan } from './plansSlice';
import { useAnalyticsData } from '../checkout/helper';
import { selectFormValidity } from '../checkout/checkoutSlice';
import { selectName } from '../family/familySlice';
import CheckoutScreen from '../checkout/CheckoutScreen';
import ReviewsBlock from './components/ReviewsBlock';
import PressLogos from './components/PressLogos';
import Guarantee from './components/GuaranteeBlock';
import FeaturesList from './components/FeaturesList';
import { fontSize, wh } from '../../mixins';
import { SecondaryTitle } from './PlansScreen';
import { useTimerNumbers, checkNumber } from '../../UI/TimerShort';
import MainHeadline from '../../UI/MainHeadline';
import UIButton from '../../UI/UIButton';

export const TERMS_LINK = 'https://familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf';
export const POLICY_LINK = 'https://terms.familo.net/privacy-info';
const site = 'https://www.familo.net/';

const NameWrapper = styled.span`
    color: #FFA361;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TimerWrapper = styled.div`
  padding: 16px 24px;
  background: rgba(255,197,23,.1);
  border: 1px solid #FFC517;
  border-radius: 16px;
  text-align: center;
  margin: 24px 0;
`;

const TimerText = styled.div`
  ${fontSize(16)}
  line-height: 19px;
  letter-spacing: -0.2px;
  color: #323232;
  white-space: pre;
`;

const TimerCount = styled.div`
  font-weight: 700;
  ${fontSize(20)}
  line-height: 24px;
  color: #FF6058;
  margin: 16px 0;
`;

const PlanWrapper = styled.div`
  padding: 13px 0;
  background: linear-gradient(92.01deg, #F58A3C 0%, #EC6029 100%);
  border-radius: 16px;
  margin-bottom: 4px;
  text-align: center;
  ${fontSize(16)}
  line-height: 1;
  color: #FEDBC3;
  
  & > div:first-child {
    color: #fff;
    font-weight: 800;
    ${fontSize(30)}
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }
`;

const spinning = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

const CheckoutWrapper = styled.div`
  margin: 40px 0 0;
  
  &:empty {
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:before {
      content: '';
      display: block;
      ${wh('48px')};
      animation: ${spinning} 1.5s linear infinite;
      background: url(${'../assets/spinner-image.svg'}) center / 100% no-repeat;
    }
  }
`;

const DisclaimerText = styled.p`
  ${fontSize(12)};
  line-height: 16px;
  color: #818181;
  margin: 16px 0 4px;
  text-align: center;

  span {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const RemarketingPaywall = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const plan = useSelector(selectPlan);
  const eventsData = useSelector(selectEventsData);
  const name = useSelector(selectName);
  const analyticsParams = useAnalyticsData();
  const isFormValid = useSelector(selectFormValidity);
  const { min, sec } = useTimerNumbers();
  const [loadCheckout, setLoadCheckout] = useState(false);
  const [plansDetails, setPlansDetails] = useState(false);
  const [eventSent, setEventSent] = useState(false);
  const paymentCallback = useRef();

  useEffect(() => dispatch(setAnalyticsData(analyticsParams)),[analyticsParams, dispatch]);

  useEffect(() => {
    sendAnalyticsEvents(EVENTS.onboardingPaywallShown, eventsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setPlan('fm-monthly-intro14d-1.99-29.99'))
    dispatch(setDefaultPlanId('fm-monthly-intro14d-1.99-29.99'))
  }, [dispatch]);

  const FEATURES = [
    t('know_when_your_partner'),
    t('track_movements'),
    t('get_sos_alert'),
    t('see_the_history_of_whereabouts'),
  ];

  useEffect(() => {
    if (plan && !eventSent) {
      const {productId, duration, description, periodPrice, price} = PLANS[plan];
      const planData = {
        Value: price,
        value: Math.round(periodPrice * 100),
        currency: 'USD',
        price: periodPrice,
        Duration: duration,
        duration,
        description,
        periodPrice,
        product_id: productId,
      };
      dispatch(setEventDataList(planData));
      sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
        ...eventsData,
        ...planData,
      });
      setPlansDetails(true);
      setEventSent(true);
    }
  }, [plan, dispatch, eventsData, eventSent]);

  useEffect(() => {
    if (plan.length > 0 && plansDetails && Object.keys(analyticsParams).length > 0 && props.paymentInstances && props.paymentRefs) {
      setTimeout(() => setLoadCheckout(true), 500);
    }
  }, [plan, analyticsParams, plansDetails, props]);

  const onContinueClick = useCallback(() => {
    paymentCallback.current?.onPurchaseClick(null);
  }, []);

  const onDisclaimerClick = useCallback(({ target: { id } }) => {
    if (id === 'terms' || id === 'refund') {
      window.open(TERMS_LINK, '_blank');
    } else if (id === 'privacy') {
      window.open(POLICY_LINK, '_blank');
    } else if (id === 'site') {
      window.open(site, '_blank');
    }
  }, []);

  const Disclaimer = useMemo(() => {
    return (
      <DisclaimerText
        dangerouslySetInnerHTML={{ __html: t('remarketing_disclaimer', { site }) }}
        onClick={onDisclaimerClick}
      />
    );
  }, [onDisclaimerClick, t]);

  return (
    <>
      <MainHeadline customStyles={'display:flex;justify-content:center;'}>{t('hey')} <NameWrapper>{decodeURIComponent(name)}!</NameWrapper></MainHeadline>
      <TimerWrapper>
        <TimerText>{t('remarketing_plan_description')}</TimerText>
        <TimerCount>{t('time_remaining')} {min}:{checkNumber(sec)}</TimerCount>
        <TimerText>{t('scroll_down')}</TimerText>
      </TimerWrapper>
      <SecondaryTitle customMargin={'0 0 20px'} >{t('unlock_unique_options')}</SecondaryTitle>
      <PlanWrapper>
        <div>{t('remarketing_plan_title')}</div>
        <div>{t('remarketing_plan_price')}</div>
      </PlanWrapper>
      <SecondaryTitle customMargin={'24px 0 12px'} >{t('what_you_get')}</SecondaryTitle>
      <FeaturesList list={FEATURES} />
      <CheckoutWrapper>
        {loadCheckout && (
            <CheckoutScreen
              implemented={true}
              paymentCallback={paymentCallback}
              {...props}
            />
        )}
      </CheckoutWrapper>
      {Disclaimer}
      <Guarantee customMargin={'30px 0'}/>
      <SecondaryTitle customMargin={'0 0 20px'}>{t('write_about_us')}</SecondaryTitle>
      <PressLogos />
      <SecondaryTitle customMargin={'0 0 16px'} >{t('customer_reviews')}</SecondaryTitle>
      <ReviewsBlock />
      <UIButton disabled={!isFormValid} onClick={onContinueClick} bottomPosition={'24px'} displayStickyBtn={false} customId={`paywall-continue-button-1`}>{t('continue')}</UIButton>
      {Disclaimer}
    </>
  )
};

export default RemarketingPaywall;
